/**
 * Яндекс Карты, JS API 2.1 — общий загрузчик для обоих приложений.
 *
 * Скрипт API подключается один раз на страницу и только там, где есть карта.
 * Ключ — браузерный: он виден в коде страницы, поэтому в кабинете разработчика
 * Яндекса он ограничен доменами приложений. Берётся из сборки
 * (`VITE_YANDEX_MAPS_KEY`) или из пула на сервере (`MapKeySource`).
 *
 * Здесь описана только та часть API, которой пользуются экраны: карта, метки
 * со своей разметкой, щелчок, подгонка под метки. Координаты — [широта, долгота].
 */

export type LatLng = [number, number]

export interface YMapEvent {
  get(key: 'coords'): LatLng
}

export interface YEvents {
  add(type: 'click', handler: (event: YMapEvent) => void): void
}

export interface YGeoObject {
  events: YEvents
}

export interface YGeoObjects {
  add(object: YGeoObject): void
  removeAll(): void
}

export interface YMap {
  geoObjects: YGeoObjects
  events: YEvents
  behaviors: { disable(name: string): void }
  getZoom(): number
  setCenter(center: LatLng, zoom?: number, options?: { duration?: number }): void
  setBounds(
    bounds: [LatLng, LatLng],
    options?: { checkZoomRange?: boolean; zoomMargin?: number },
  ): void
  destroy(): void
}

export interface YLayoutClass {
  readonly template?: string
}

export interface YMaps {
  ready(onReady: () => void, onError?: (error: unknown) => void): void
  Map: new (
    element: HTMLElement,
    state: { center: LatLng; zoom: number; controls: string[] },
    options?: { suppressMapOpenBlock?: boolean; maxZoom?: number },
  ) => YMap
  Placemark: new (
    coordinates: LatLng,
    properties: { hintContent?: string },
    options: {
      iconLayout: YLayoutClass
      iconShape: { type: 'Circle'; coordinates: LatLng; radius: number }
      zIndex?: number
    },
  ) => YGeoObject
  templateLayoutFactory: { createClass(template: string): YLayoutClass }
}

declare global {
  interface Window {
    ymaps?: YMaps
  }
}

/** Центр Ташкента: сюда смотрит карта, пока точек нет. */
export const TASHKENT: LatLng = [41.3111, 69.2797]

/**
 * Откуда брать ключ. Тот же контракт — у источника в `@dorixona/api/maps`:
 * ключ из пула на сервере, а когда Яндекс ему отказал — следующий.
 */
export interface MapKeySource {
  /** Ключ, с которым грузить; пустая строка — ключа нет. */
  key(): Promise<string>
  /** Яндекс не пустил с этим ключом — ключ, который пробовать теперь; '' — пробовать нечего. */
  refused(key: string): Promise<string>
}

/**
 * Сбой, который не запоминаем: ключа нет или скрипт не пришёл (сеть или
 * отказ Яндекса 403/429 — по тегу их не различить). Следующая карта пробует снова.
 */
class Retryable extends Error {}

let loading: Promise<YMaps> | null = null

/** Готовый API: ждём `ymaps.ready`, модули API грузятся после самого скрипта. */
function whenReady(api: YMaps): Promise<YMaps> {
  return new Promise((resolve, reject) => {
    api.ready(() => {
      resolve(api)
    }, reject)
  })
}

function inject(apiKey: string, lang: string): Promise<YMaps> {
  return new Promise<YMaps>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=${lang}`
    script.async = true
    script.onload = () => {
      if (window.ymaps) resolve(whenReady(window.ymaps))
      else reject(new Error('Yandex Maps API did not start'))
    }
    script.onerror = () => {
      script.remove()
      reject(new Retryable('Yandex Maps API did not load'))
    }
    document.head.appendChild(script)
  })
}

/**
 * Скрипт с ключом источника. Не пришёл — возможно, Яндекс отказал ключу:
 * говорим источнику и один раз пробуем ключ, который он даст взамен. Сеть
 * это или отказ, источник (сервер) проверит сам.
 */
async function loadWith(source: MapKeySource, lang: string): Promise<YMaps> {
  const key = await source.key().catch((error: unknown) => {
    throw new Retryable(error instanceof Error ? error.message : 'Yandex Maps key failed')
  })
  if (!key) throw new Retryable('Yandex Maps key is empty')
  try {
    return await inject(key, lang)
  } catch (error) {
    // «Ключа нет» сюда не попадает: ключ был, отказал скрипт.
    if (!(error instanceof Retryable)) throw error
    const next = await source.refused(key)
    if (!next || next === key) throw error
    return inject(next, lang)
  }
}

/**
 * Подключает API один раз на страницу. Без ключа не грузим вовсе: карта
 * скажет, что её нет, вместо ошибки от Яндекса. Сбой сети или ключа не
 * запоминаем — следующая карта попробует снова.
 */
export function loadYandexMaps(key: string | MapKeySource, lang = 'ru_RU'): Promise<YMaps> {
  if (window.ymaps) return whenReady(window.ymaps)
  const source: MapKeySource =
    typeof key === 'string'
      ? { key: () => Promise.resolve(key), refused: () => Promise.resolve('') }
      : key
  loading ??= loadWith(source, lang).catch((error: unknown) => {
    if (error instanceof Retryable) loading = null
    throw error
  })
  return loading
}

/** Для тестов: забыть подключённый скрипт. */
export function resetYandexMapsLoader(): void {
  loading = null
}
